import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchDashboardData } from '../store/slices/dashboardSlice';
import { branding } from '../constants/branding';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import type { DashboardState, Account, Transaction } from '../store/slices/dashboardSlice';
import type { RootState } from '../store';
import { formatAmount } from '../utils/currency';
import { adminApiService } from '../services/adminApiService';
import type { DashboardStatsDto } from '../types/dashboard';
import StatCard from '../components/StatCard';
import { useAdminApiService } from '../hooks/useAdminApiService';
import { useAccessControl } from '../utils/accessControl';
import { apiService } from '../services/apiService';
import { featureFlags } from '../config/featureFlags';
import QRCode from 'qrcode';
import { getTrueLayerPaymentReturnUri } from '../utils/apiConfig';
import { runTrueLayerPaymentAuthorizationLoop } from '../utils/truelayerPaymentAuth';

const getPrimaryCurrency = (accounts: Account[]): string => {
  // Try to find a checking account first
  const checkingAccount = accounts.find(acc => acc.accountType.toLowerCase() === 'checking');
  if (checkingAccount?.currency) {
    return checkingAccount.currency;
  }
  // Otherwise use the first account's currency, or default to USD
  return accounts[0]?.currency || 'USD';
};

const mapTrueLayerErrorMessage = (rawError?: string): string => {
  const input = (rawError || '').trim();
  const lowered = input.toLowerCase();

  if (!input) return 'Something went wrong with bank connectivity. Please try again.';
  if (lowered.includes('reconnect') || lowered.includes('token') || lowered.includes('expired')) {
    return 'Your bank consent expired. Please reconnect your bank account.';
  }
  if (lowered.includes('401') || lowered.includes('unauthor')) {
    return 'Your session expired. Please sign in again and retry.';
  }
  if (lowered.includes('403') || lowered.includes('forbidden')) {
    return 'You do not have access to this action.';
  }
  if (lowered.includes('404') || lowered.includes('not found')) {
    return 'Requested bank data was not found.';
  }
  if (lowered.includes('422') || lowered.includes('validation')) {
    return 'Some payment details are invalid. Please review and try again.';
  }
  if (lowered.includes('500') || lowered.includes('internal server')) {
    return 'Bank service is temporarily unavailable. Please try again shortly.';
  }
  return input;
};

const isLikelyIban = (value: string): boolean => {
  const compact = value.replace(/\s+/g, '').toUpperCase();
  return /^[A-Z]{2}\d{2}[A-Z0-9]{7,30}$/.test(compact);
};

const buildShortPaymentReference = (prefix: string): string => {
  const cleanPrefix = (prefix || 'pm').replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toLowerCase() || 'pm';
  const ts = Date.now().toString(36).slice(-8);
  const rnd = Math.floor(Math.random() * 1296).toString(36).padStart(2, '0');
  // Format keeps under TrueLayer 18-char reference limit.
  return `${cleanPrefix}-${ts}-${rnd}`.slice(0, 18);
};

/** Match subscription chain hint (e.g. ethereum) to Crypto page wallet blockchain (e.g. Ethereum). */
const cryptoChainsMatch = (chainHint: string, walletBlockchain: string): boolean => {
  const a = (chainHint || '').trim().toLowerCase();
  const b = (walletBlockchain || '').trim().toLowerCase();
  if (!a || !b) return false;
  if (a === b) return true;
  const norm = (c: string) =>
    c === 'eth' || c === 'ethereum'
      ? 'ethereum'
      : c === 'matic' || c === 'polygon'
        ? 'polygon'
        : c === 'btc' || c === 'bitcoin'
          ? 'bitcoin'
          : c;
  return norm(a) === norm(b);
};

const trackTrueLayerEvent = (
  eventName: string,
  payload?: Record<string, string | number | boolean | null | undefined>
) => {
  const safePayload = Object.entries(payload || {}).reduce<Record<string, string | number | boolean | null>>(
    (acc, [key, value]) => {
      if (value === undefined) return acc;
      const lowered = key.toLowerCase();
      if (
        lowered.includes('token') ||
        lowered.includes('iban') ||
        lowered.includes('email') ||
        lowered.includes('name')
      ) {
        return acc;
      }
      acc[key] = value;
      return acc;
    },
    {}
  );

  console.info('[TrueLayerTelemetry]', {
    eventName,
    timestamp: new Date().toISOString(),
    ...safePayload,
  });
};

type PlanPaymentSetting = 'ALL' | 'STRIPE' | 'CRYPTO';

const parsePlanPaymentMethods = (raw: unknown): PlanPaymentSetting => {
  const u = String(raw ?? 'ALL').trim().toUpperCase();
  if (u === 'STRIPE' || u === 'CRYPTO' || u === 'ALL') return u;
  return 'ALL';
};

const allowedSubscriptionPaymentMethods = (pm: PlanPaymentSetting): Array<'STRIPE' | 'CRYPTO'> => {
  if (pm === 'STRIPE') return ['STRIPE'];
  if (pm === 'CRYPTO') return ['CRYPTO'];
  return ['STRIPE', 'CRYPTO'];
};

const Dashboard: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedTxId, setExpandedTxId] = useState<number | null>(null);
  const [tradingBotBalance, setTradingBotBalance] = useState<{allocated: number, available: number} | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [retailPlansForUpgrade, setRetailPlansForUpgrade] = useState<
    Array<{
      id: number;
      name: string;
      priceMonthly: number;
      currency: string;
      canSelectAsUpgrade?: boolean;
      paymentMethods: PlanPaymentSetting;
    }>
  >([]);
  const [selectedUpgradePlanId, setSelectedUpgradePlanId] = useState<number | null>(null);
  const [upgradeFlowBusy, setUpgradeFlowBusy] = useState(false);
  const [upgradeFlowError, setUpgradeFlowError] = useState<string | null>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [pendingUpgradeInfo, setPendingUpgradeInfo] = useState<{
    hasPending?: boolean;
    status?: string;
    upgradeRequestId?: number;
    paymentMethod?: 'STRIPE' | 'CRYPTO' | string;
    checkoutUrl?: string;
    destinationAddress?: string;
    blockchain?: string;
    symbol?: string;
    amount?: number | string;
    currency?: string;
    message?: string;
    targetPlanName?: string;
  } | null>(null);
  const [subscriptionPaymentMethod, setSubscriptionPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [cryptoBlockchain, setCryptoBlockchain] = useState('ethereum');
  const [cryptoSymbol, setCryptoSymbol] = useState('USDT');
  const [cryptoTxHash, setCryptoTxHash] = useState('');
  const [cryptoSubmitBusy, setCryptoSubmitBusy] = useState(false);
  const [cryptoSubmitError, setCryptoSubmitError] = useState<string | null>(null);
  const [cryptoWalletPayBusy, setCryptoWalletPayBusy] = useState(false);
  const [cryptoWalletPayError, setCryptoWalletPayError] = useState<string | null>(null);
  const [subscriptionPayFromWallet, setSubscriptionPayFromWallet] = useState<{
    address: string;
    blockchain: string;
  } | null>(null);
  const [subscriptionWalletLoading, setSubscriptionWalletLoading] = useState(false);
  const [subscriptionWalletLoadError, setSubscriptionWalletLoadError] = useState<string | null>(null);
  const [subscriptionDestQrDataUrl, setSubscriptionDestQrDataUrl] = useState<string | null>(null);
  const [subscriptionUserWalletQrDataUrl, setSubscriptionUserWalletQrDataUrl] = useState<string | null>(null);
  const [isConnectingBank, setIsConnectingBank] = useState(false);
  const [connectBankError, setConnectBankError] = useState<string | null>(null);
  const [isRefreshingBankStatus, setIsRefreshingBankStatus] = useState(false);
  const [isBankConnected, setIsBankConnected] = useState<boolean | null>(null);
  const [linkedBankAccountsCount, setLinkedBankAccountsCount] = useState(0);
  const [linkedBankAccounts, setLinkedBankAccounts] = useState<Array<{
    accountId: string;
    displayName: string;
    providerName: string;
    currency: string;
  }>>([]);
  const [selectedLinkedAccountId, setSelectedLinkedAccountId] = useState<string | null>(null);
  const [linkedBalance, setLinkedBalance] = useState<{
    currency: string;
    available: number | null;
    current: number | null;
    overdraft: number | null;
    updateTimestamp: string | null;
  } | null>(null);
  const [isLinkedBalanceLoading, setIsLinkedBalanceLoading] = useState(false);
  const [linkedBalanceError, setLinkedBalanceError] = useState<string | null>(null);
  const [linkedTransactions, setLinkedTransactions] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    amount: number;
    currency: string;
  }>>([]);
  const [isLinkedTransactionsLoading, setIsLinkedTransactionsLoading] = useState(false);
  const [linkedTransactionsError, setLinkedTransactionsError] = useState<string | null>(null);
  const [linkedTxSearch, setLinkedTxSearch] = useState('');
  const [linkedTxFromDate, setLinkedTxFromDate] = useState('');
  const [linkedTxToDate, setLinkedTxToDate] = useState('');
  const [paymentAmountMajor, setPaymentAmountMajor] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('EUR');
  const [paymentBeneficiaryName, setPaymentBeneficiaryName] = useState('');
  const [paymentBeneficiaryIban, setPaymentBeneficiaryIban] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [isCreatingTrueLayerPayment, setIsCreatingTrueLayerPayment] = useState(false);
  const [createPaymentError, setCreatePaymentError] = useState<string | null>(null);
  const [createdPaymentResult, setCreatedPaymentResult] = useState<{
    paymentId: string;
    resourceToken?: string;
    status?: string;
    reference?: string;
    createdAtMs: number;
  } | null>(null);
  const [isStartingPaymentAuthorization, setIsStartingPaymentAuthorization] = useState(false);
  const [paymentAuthorizationError, setPaymentAuthorizationError] = useState<string | null>(null);
  const [paymentAuthorizationUrl, setPaymentAuthorizationUrl] = useState<string | null>(null);
  const [paymentProviderSelectionRequired, setPaymentProviderSelectionRequired] = useState(false);
  const [paymentLifecycleStatus, setPaymentLifecycleStatus] = useState<string | null>(null);
  const [isPollingPaymentStatus, setIsPollingPaymentStatus] = useState(false);
  const [paymentPollingMessage, setPaymentPollingMessage] = useState<string | null>(null);
  const paymentPollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paymentPollingStartedAtRef = useRef<number | null>(null);
  const [bankStatusMessage, setBankStatusMessage] = useState<string | null>(null);
  const [requiresReconnect, setRequiresReconnect] = useState(false);
  const PAYMENT_CONTEXT_TTL_MS = 10 * 60 * 1000;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { user, isAuthenticated, token, type: authType, isLoading: authLoading } = useAppSelector(
    (state: RootState) => state.auth
  );
  const { data: dashboardData, isLoading, error } = useAppSelector(state => state.dashboard as DashboardState);
  const profile = useAppSelector((state: RootState) => state.profile.profile);
  const { isChildAccount } = useAccessControl();
  
  // Get user permissions for dashboard section filtering
  const userPermissions: string[] = (profile as any)?.permissions || [];
  const showTrueLayerBankPanel =
    featureFlags.retail.enableTrueLayerRetail &&
    userPermissions.includes('VIEW_RETAIL_DASHBOARD_TRUELAYER');

  // Debug: Log user permissions to console
  console.log('Dashboard - User permissions:', userPermissions);
  console.log('Dashboard - Profile data:', profile);

  // Helper function to get available investment features based on permissions
  const getAvailableInvestmentFeatures = () => {
    const features = [];
    
    // Check if user has any specific investment permissions
    const hasSpecificPermissions = userPermissions.includes('VIEW_RETAIL_CRYPTO') || 
                                   userPermissions.includes('VIEW_RETAIL_ASSETS') || 
                                   userPermissions.includes('VIEW_RETAIL_TRADING_BOTS') || 
                                   userPermissions.includes('VIEW_RETAIL_SAVINGS');
    
    // If user has specific permissions, only show those features
    // If user only has legacy permission (no specific permissions), show all features
    const hasLegacyPermission = userPermissions.includes('VIEW_RETAIL_DASHBOARD_INVESTMENTS');
    
    if (hasSpecificPermissions) {
      // User has specific permissions - only show those features
      if (userPermissions.includes('VIEW_RETAIL_CRYPTO')) {
        features.push('crypto');
      }
      if (userPermissions.includes('VIEW_RETAIL_ASSETS')) {
        features.push('assets');
      }
      if (userPermissions.includes('VIEW_RETAIL_TRADING_BOTS')) {
        features.push('tradingBots');
      }
      if (userPermissions.includes('VIEW_RETAIL_SAVINGS')) {
        features.push('savings');
      }
    } else if (hasLegacyPermission) {
      // User only has legacy permission - show all features
      features.push('crypto', 'assets', 'tradingBots', 'savings');
    }
    
    return features;
  };

  const availableInvestmentFeatures = getAvailableInvestmentFeatures();
  const hasDesktopLeftContent =
    userPermissions.includes('VIEW_RETAIL_DASHBOARD_BALANCE') ||
    showTrueLayerBankPanel ||
    userPermissions.includes('VIEW_RETAIL_DASHBOARD_ACCOUNTS') ||
    userPermissions.includes('VIEW_RETAIL_DASHBOARD_TRANSACTIONS');
  
  // Debug: Log available investment features
  console.log('Dashboard - Available investment features:', availableInvestmentFeatures);
  console.log('Dashboard - Has specific permissions:', userPermissions.includes('VIEW_RETAIL_CRYPTO') || 
                                   userPermissions.includes('VIEW_RETAIL_ASSETS') || 
                                   userPermissions.includes('VIEW_RETAIL_TRADING_BOTS') || 
                                   userPermissions.includes('VIEW_RETAIL_SAVINGS'));
  console.log('Dashboard - Has legacy permission:', userPermissions.includes('VIEW_RETAIL_DASHBOARD_INVESTMENTS'));
  
  // More robust fetch tracking
  const hasFetchedData = useRef(false);
  const isFetching = useRef(false);
  const lastFetchTime = useRef<number>(0);
  const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches
  const subscriptionFetchedForUserId = useRef<number | null>(null);
  const tradingBalanceFetchKeyRef = useRef<string>('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const now = Date.now();
    console.log('Dashboard useEffect triggered:', { 
      isAuthenticated, 
      hasFetchedData: hasFetchedData.current,
      isFetching: isFetching.current,
      dashboardData: !!dashboardData,
      timeSinceLastFetch: now - lastFetchTime.current
    });
    
    // Prevent multiple fetches
    if (isFetching.current) {
      console.log('Dashboard: Already fetching, skipping...');
      return;
    }
    
    // Check cooldown period
    if (now - lastFetchTime.current < FETCH_COOLDOWN) {
      console.log('Dashboard: In cooldown period, skipping...');
      return;
    }
    
    // JWT: wait until a real bearer token exists (avoids /dashboard 401 during auth hydration).
    const authReadyForDashboard =
      authType === 'SESSION' || (authType === 'JWT' && !!token && token !== 'SESSION_AUTH');

    if (
      isAuthenticated &&
      authReadyForDashboard &&
      profile?.id &&
      !hasFetchedData.current &&
      !dashboardData
    ) {
      if (isFetching.current) {
        return;
      }
      console.log('Dashboard: Fetching data...');
      hasFetchedData.current = true;
      isFetching.current = true;
      lastFetchTime.current = now;
      dispatch(fetchDashboardData());
    } else if (isAuthenticated && dashboardData) {
      // If we already have data, mark as fetched to prevent refetching
      hasFetchedData.current = true;
    }
  }, [dispatch, isAuthenticated, authType, token, profile?.id, dashboardData]);

  // Reset the fetch flag when user logs out
  useEffect(() => {
    console.log('Dashboard logout effect:', { isAuthenticated });
    if (!isAuthenticated) {
      hasFetchedData.current = false;
      isFetching.current = false;
      lastFetchTime.current = 0;
    }
  }, [isAuthenticated]);

  // Reset fetching flag when data is loaded
  useEffect(() => {
    if (dashboardData && isFetching.current) {
      console.log('Dashboard: Data loaded, resetting fetch flag');
      isFetching.current = false;
    }
  }, [dashboardData]);

  // Reset fetching flag when there's an error
  useEffect(() => {
    if (error && isFetching.current) {
      console.log('Dashboard: Error occurred, resetting fetch flag');
      isFetching.current = false;
    }
  }, [error]);

  // Component lifecycle debugging
  useEffect(() => {
    console.log('Dashboard component mounted');
    return () => {
      console.log('Dashboard component unmounted');
    };
  }, []);

  // Subscription: once per user session on this mount (avoid re-fetch when permissions array populates)
  useEffect(() => {
    if (!isAuthenticated || !profile?.id) {
      subscriptionFetchedForUserId.current = null;
      return;
    }
    const uid = profile.id;
    if (subscriptionFetchedForUserId.current === uid) {
      return;
    }
    subscriptionFetchedForUserId.current = uid;
    void fetchSubscriptionStatus();
  }, [isAuthenticated, profile?.id]);

  // Trading wallet: when permission / user context changes, dedupe identical keys
  useEffect(() => {
    if (!isAuthenticated || !profile?.id || isChildAccount) {
      tradingBalanceFetchKeyRef.current = '';
      setTradingBotBalance(null);
      return;
    }
    const canFetchTradingWallet =
      userPermissions.includes('VIEW_RETAIL_TRADING_BOTS');
    const permsKey = userPermissions.join('|');
    const fetchKey = `${profile.id}|${canFetchTradingWallet}|${permsKey}`;
    if (!canFetchTradingWallet) {
      setTradingBotBalance(null);
      return;
    }
    if (tradingBalanceFetchKeyRef.current === fetchKey) {
      return;
    }
    tradingBalanceFetchKeyRef.current = fetchKey;
    void fetchTradingBotBalance();
  }, [isAuthenticated, isChildAccount, profile?.id, userPermissions.join('|')]);

  const handleCryptoClick = () => navigate('/crypto');
  const handleAssetsClick = () => navigate('/assets');
  const handleSavingsClick = () => navigate('/savings');
  const handleTradingBotsClick = () => navigate('/trading-bots');

  // Fetch trading bot balance (backend requires VIEW_RETAIL_TRADING_BOTS — only call when permitted)
  const fetchTradingBotBalance = async () => {
    const userId = profile?.id;
    if (!userId) {
      console.error('No user ID available for trading bot balance fetch');
      return;
    }
    if (!userPermissions.includes('VIEW_RETAIL_TRADING_BOTS')) {
      setTradingBotBalance(null);
      return;
    }
    try {
      const response = await apiService.get(`/api/retail/trading/wallet-balance/${userId}`);
      if (response.success && response.data) {
        console.log('Setting trading bot balance:', response.data);
        setTradingBotBalance({
          allocated: response.data.allocatedBalance || 0,
          available: response.data.availableBalance || 0
        });
      } else {
        console.warn('Trading wallet balance unavailable:', response.error || response.message);
        setTradingBotBalance(null);
      }
    } catch (error) {
      console.error('Failed to fetch trading bot balance:', error);
      setTradingBotBalance(null);
    }
  };

  // Fetch subscription status for the logged-in retail user
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await apiService.get('/api/retail/subscription/status');
      if (response.success && response.data) {
        setSubscriptionStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  };

  const loadPendingUpgrade = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiService.get('/api/retail/subscription/upgrade/pending');
      if (res.success && res.data) {
        setPendingUpgradeInfo(res.data as any);
      }
    } catch {
      /* non-blocking */
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadPendingUpgrade();
    const t = window.setInterval(() => void loadPendingUpgrade(), 45000);
    return () => window.clearInterval(t);
  }, [loadPendingUpgrade]);

  useEffect(() => {
    let cancelled = false;
    const loadCryptoPaymentVisuals = async () => {
      const p = pendingUpgradeInfo;
      if (
        !p?.hasPending ||
        p.status !== 'AWAITING_PAYMENT' ||
        String(p.paymentMethod || '').toUpperCase() !== 'CRYPTO'
      ) {
        setSubscriptionPayFromWallet(null);
        setSubscriptionDestQrDataUrl(null);
        setSubscriptionUserWalletQrDataUrl(null);
        setSubscriptionWalletLoadError(null);
        setSubscriptionWalletLoading(false);
        return;
      }

      const chainHint = String(p.blockchain || (p as any).cryptoBlockchain || '').trim();
      const dest = String(p.destinationAddress || (p as any).cryptoDestinationAddress || '').trim();

      setSubscriptionWalletLoading(true);
      setSubscriptionWalletLoadError(null);

      try {
        if (dest.length > 0) {
          const dqr = await QRCode.toDataURL(dest, { width: 180, margin: 2, errorCorrectionLevel: 'M' });
          if (!cancelled) setSubscriptionDestQrDataUrl(dqr);
        } else if (!cancelled) {
          setSubscriptionDestQrDataUrl(null);
        }
      } catch {
        if (!cancelled) setSubscriptionDestQrDataUrl(null);
      }

      try {
        const res = await apiService.get<Array<{ blockchain?: string; address?: string }>>('/crypto/wallet');
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const match = list.find((w) => cryptoChainsMatch(chainHint, String(w.blockchain || '')));
        if (match?.address) {
          setSubscriptionPayFromWallet({
            address: match.address,
            blockchain: String(match.blockchain || chainHint),
          });
          try {
            const uqr = await QRCode.toDataURL(match.address, {
              width: 180,
              margin: 2,
              errorCorrectionLevel: 'M',
            });
            if (!cancelled) setSubscriptionUserWalletQrDataUrl(uqr);
          } catch {
            if (!cancelled) setSubscriptionUserWalletQrDataUrl(null);
          }
        } else {
          setSubscriptionPayFromWallet(null);
          setSubscriptionUserWalletQrDataUrl(null);
        }
      } catch {
        if (!cancelled) {
          setSubscriptionWalletLoadError('Could not load your Zogzila Crypto wallets.');
          setSubscriptionPayFromWallet(null);
          setSubscriptionUserWalletQrDataUrl(null);
        }
      } finally {
        if (!cancelled) setSubscriptionWalletLoading(false);
      }
    };

    void loadCryptoPaymentVisuals();
    return () => {
      cancelled = true;
    };
  }, [
    pendingUpgradeInfo?.hasPending,
    pendingUpgradeInfo?.status,
    pendingUpgradeInfo?.paymentMethod,
    pendingUpgradeInfo?.blockchain,
    pendingUpgradeInfo?.destinationAddress,
    (pendingUpgradeInfo as any)?.cryptoBlockchain,
    (pendingUpgradeInfo as any)?.cryptoDestinationAddress,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripe = (params.get('stripe') || '').toLowerCase();
    const upgrade = params.get('upgrade');
    const rawHash = window.location.hash || '';
    const hashLower = rawHash.toLowerCase();
    const isUpgradeReturn =
      (stripe === 'success' && upgrade === '1') ||
      (hashLower.includes('stripe=success') && hashLower.includes('upgrade=1'));
    if (!isUpgradeReturn) {
      return;
    }
    if (authLoading || !isAuthenticated) {
      return;
    }

    let sessionIdFromHash: string | null = null;
    const hashQuery = rawHash.includes('?') ? rawHash.split('?')[1] : '';
    if (hashQuery) {
      sessionIdFromHash = new URLSearchParams(hashQuery).get('session_id');
    }
    const sessionId = params.get('session_id') || sessionIdFromHash;

    void (async () => {
      try {
        const res = await apiService.post<Record<string, unknown>>(
          '/api/retail/subscription/upgrade/confirm-payment',
          sessionId ? { sessionId } : {}
        );
        if (res.success && res.data && typeof res.data === 'object') {
          setPendingUpgradeInfo(res.data as any);
        } else {
          await loadPendingUpgrade();
        }
      } catch (e) {
        console.warn('Upgrade payment confirm failed (webhook may still apply):', e);
        await loadPendingUpgrade();
      }
      await fetchSubscriptionStatus();
      if (window.history.replaceState) {
        window.history.replaceState({}, '', window.location.pathname || '/dashboard');
      }
    })();
  }, [loadPendingUpgrade, authLoading, isAuthenticated]);

  const subscriptionUpgradeEligible =
    subscriptionStatus &&
    (subscriptionStatus.status === 'ACTIVE' || subscriptionStatus.status === 'GRACE_PERIOD') &&
    subscriptionStatus.planId != null;

  /** Same paid flow as upgrade (compliance + Stripe), when there is no active/grace subscription. */
  const subscriptionRenewEligible =
    subscriptionStatus &&
    subscriptionStatus.planId != null &&
    !pendingUpgradeInfo?.hasPending &&
    (subscriptionStatus.status === 'EXPIRED' ||
      subscriptionStatus.status === 'SUSPENDED' ||
      subscriptionStatus.status === 'CANCELLED');

  const renewalPaymentMethodsSetting = useMemo(
    () =>
      parsePlanPaymentMethods(
        subscriptionStatus?.registrationPaymentMethods ?? subscriptionStatus?.registration_payment_methods
      ),
    [subscriptionStatus]
  );
  const renewalAllowedPaymentMethods = useMemo((): Array<'STRIPE' | 'CRYPTO'> => {
    return allowedSubscriptionPaymentMethods(renewalPaymentMethodsSetting);
  }, [renewalPaymentMethodsSetting]);

  const upgradeAllowedPaymentMethods = useMemo((): Array<'STRIPE' | 'CRYPTO'> => {
    if (!showUpgradeModal) return ['STRIPE', 'CRYPTO'];
    const p = retailPlansForUpgrade.find((x) => x.id === selectedUpgradePlanId);
    if (!p) return ['STRIPE', 'CRYPTO'];
    return allowedSubscriptionPaymentMethods(p.paymentMethods);
  }, [showUpgradeModal, selectedUpgradePlanId, retailPlansForUpgrade]);

  useEffect(() => {
    if (showUpgradeModal) {
      if (!upgradeAllowedPaymentMethods.includes(subscriptionPaymentMethod)) {
        setSubscriptionPaymentMethod(upgradeAllowedPaymentMethods[0]);
      }
      return;
    }
    if (subscriptionRenewEligible) {
      if (!renewalAllowedPaymentMethods.includes(subscriptionPaymentMethod)) {
        setSubscriptionPaymentMethod(renewalAllowedPaymentMethods[0]);
      }
    }
  }, [
    showUpgradeModal,
    subscriptionRenewEligible,
    upgradeAllowedPaymentMethods,
    renewalAllowedPaymentMethods,
    subscriptionPaymentMethod,
  ]);

  const parseApiMoney = (v: unknown): number => {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(String(v).replace(',', '.').trim());
      return Number.isFinite(n) ? n : NaN;
    }
    return NaN;
  };

  const openUpgradeModal = async () => {
    setUpgradeFlowError(null);
    setSelectedUpgradePlanId(null);
    setShowUpgradeModal(true);
    try {
      const res = await apiService.get('/api/retail/subscription/plans');
      if (res.success && Array.isArray(res.data)) {
        const currentPlanIdRaw = subscriptionStatus?.planId;
        const currentPlanId =
          currentPlanIdRaw != null && currentPlanIdRaw !== ''
            ? Number(currentPlanIdRaw)
            : NaN;
        const currentPrice = parseApiMoney(subscriptionStatus?.price);

        const others = (res.data as any[])
          .map((p) => {
            const id = Number(p.id);
            const priceMonthly = parseApiMoney(p.priceMonthly ?? p.price_monthly);
            const active = p.is_active !== false && p.active !== false;
            return {
              id,
              name: String(p.name ?? 'Plan'),
              priceMonthly,
              currency: (p.currency as string) || 'EUR',
              active,
              paymentMethods: parsePlanPaymentMethods(p.registrationPaymentMethods ?? p.registration_payment_methods),
            };
          })
          .filter((p) => p.active && Number.isFinite(p.id))
          .filter((p) => !Number.isFinite(currentPlanId) || p.id !== currentPlanId)
          .sort((a, b) => a.priceMonthly - b.priceMonthly);

        const withFlags = others.map((p) => ({
          ...p,
          /** Strict upgrade = higher monthly price than current subscription (backend enforces too). */
          canSelectAsUpgrade:
            Number.isFinite(currentPrice) && Number.isFinite(p.priceMonthly)
              ? p.priceMonthly > currentPrice
              : Number.isFinite(p.priceMonthly),
        }));

        setRetailPlansForUpgrade(withFlags);
        if (withFlags.length > 0 && !withFlags.some((x) => x.canSelectAsUpgrade)) {
          setUpgradeFlowError(
            'No higher-priced plan is available, or your current plan price could not be read. If you expect an upgrade option, contact support.'
          );
        }
      } else {
        setRetailPlansForUpgrade([]);
      }
    } catch {
      setUpgradeFlowError('Could not load available plans.');
      setRetailPlansForUpgrade([]);
    }
  };

  const selectedUpgradePlan = retailPlansForUpgrade.find((p) => p.id === selectedUpgradePlanId);
  const canSubmitUpgrade = !!selectedUpgradePlan?.canSelectAsUpgrade;
  const isCryptoPaymentMethod = subscriptionPaymentMethod === 'CRYPTO';

  const buildPaymentMethodPayload = () => {
    if (subscriptionPaymentMethod === 'CRYPTO') {
      return {
        paymentMethod: 'CRYPTO',
        blockchain: cryptoBlockchain,
        symbol: cryptoSymbol,
      };
    }
    return { paymentMethod: 'STRIPE' };
  };

  const confirmPlanUpgrade = async () => {
    if (selectedUpgradePlanId == null || !canSubmitUpgrade) return;
    const planRow = retailPlansForUpgrade.find((x) => x.id === selectedUpgradePlanId);
    const allowedForPlan = planRow
      ? allowedSubscriptionPaymentMethods(planRow.paymentMethods)
      : (['STRIPE', 'CRYPTO'] as Array<'STRIPE' | 'CRYPTO'>);
    if (!allowedForPlan.includes(subscriptionPaymentMethod)) {
      setUpgradeFlowError('This plan does not allow the selected payment method.');
      return;
    }
    setUpgradeFlowBusy(true);
    setUpgradeFlowError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/upgrade/start', {
        targetPlanId: selectedUpgradePlanId,
        ...buildPaymentMethodPayload(),
      });
      if (!res.success) {
        setUpgradeFlowError((res as any).error || 'Upgrade could not be started.');
        return;
      }
      const data = res.data as any;
      if (data?.requiresCompliance) {
        setShowUpgradeModal(false);
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.checkoutUrl) {
        window.open(data.checkoutUrl as string, '_blank', 'noopener,noreferrer');
        setShowUpgradeModal(false);
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.paymentMethod === 'CRYPTO') {
        setShowUpgradeModal(false);
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      }
    } catch (err: any) {
      setUpgradeFlowError(err?.message || 'Upgrade could not be started.');
    } finally {
      setUpgradeFlowBusy(false);
    }
  };

  const startRenewalFlow = async () => {
    const pid = subscriptionStatus?.planId;
    if (pid == null || renewBusy) return;
    if (!renewalAllowedPaymentMethods.includes(subscriptionPaymentMethod)) {
      setRenewError('This plan does not allow the selected payment method.');
      return;
    }
    setRenewBusy(true);
    setRenewError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/renew/start', {
        planId: Number(pid),
        ...buildPaymentMethodPayload(),
      });
      if (!res.success) {
        setRenewError((res as any).error || 'Renewal could not be started.');
        return;
      }
      const data = res.data as any;
      if (data?.requiresCompliance) {
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.checkoutUrl) {
        window.open(data.checkoutUrl as string, '_blank', 'noopener,noreferrer');
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      } else if (data?.paymentMethod === 'CRYPTO') {
        await loadPendingUpgrade();
        await fetchSubscriptionStatus();
      }
    } catch (err: any) {
      setRenewError(err?.message || 'Renewal could not be started.');
    } finally {
      setRenewBusy(false);
    }
  };

  const submitCryptoPaymentProof = async () => {
    const upgradeRequestId = pendingUpgradeInfo?.upgradeRequestId ?? (pendingUpgradeInfo as any)?.requestId;
    if (!upgradeRequestId || !cryptoTxHash.trim() || cryptoSubmitBusy) return;
    setCryptoSubmitBusy(true);
    setCryptoSubmitError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/upgrade/submit-crypto-payment', {
        upgradeRequestId,
        txHash: cryptoTxHash.trim(),
      });
      if (!res.success) {
        setCryptoSubmitError((res as any).error || 'Could not submit transaction hash.');
        return;
      }
      setCryptoTxHash('');
      await loadPendingUpgrade();
      await fetchSubscriptionStatus();
    } catch (err: any) {
      setCryptoSubmitError(err?.message || 'Could not submit transaction hash.');
    } finally {
      setCryptoSubmitBusy(false);
    }
  };

  const payUpgradeWithIntegratedCryptoWallet = async () => {
    const upgradeRequestId = pendingUpgradeInfo?.upgradeRequestId ?? (pendingUpgradeInfo as any)?.requestId;
    if (!upgradeRequestId || cryptoWalletPayBusy) return;
    setCryptoWalletPayBusy(true);
    setCryptoWalletPayError(null);
    try {
      const res = await apiService.post('/api/retail/subscription/upgrade/pay-with-wallet', {
        upgradeRequestId,
      });
      if (!res.success) {
        setCryptoWalletPayError((res as any).error || 'Could not execute wallet payment.');
        return;
      }
      await loadPendingUpgrade();
      await fetchSubscriptionStatus();
    } catch (err: any) {
      setCryptoWalletPayError(err?.message || 'Could not execute wallet payment.');
    } finally {
      setCryptoWalletPayBusy(false);
    }
  };

  const handleConnectBankAccount = async () => {
    if (isConnectingBank) return;
    trackTrueLayerEvent('truelayer_connect_clicked', { source: 'dashboard' });
    setIsConnectingBank(true);
    setConnectBankError(null);
    try {
      const response = await apiService.get<{ authUrl: string }>('/api/banking/truelayer/auth/url');
      if (!response.success || !response.data?.authUrl) {
        setConnectBankError(mapTrueLayerErrorMessage(response.error || 'Failed to generate bank connection URL.'));
        return;
      }
      setRequiresReconnect(false);
      window.location.href = response.data.authUrl;
    } catch (err) {
      setConnectBankError(mapTrueLayerErrorMessage('Failed to generate bank connection URL.'));
    } finally {
      setIsConnectingBank(false);
    }
  };

  const refreshBankConnectionStatus = async (source: 'consent_return' | 'manual_refresh' | 'auto_refresh' = 'manual_refresh') => {
    if (isRefreshingBankStatus) return;
    trackTrueLayerEvent('truelayer_account_fetch_started', { source });
    setIsRefreshingBankStatus(true);
    setBankStatusMessage(null);
    try {
      const response = await apiService.get<{ results?: Array<any> }>('/api/banking/truelayer/accounts');
      if (!response.success) {
        const msg = mapTrueLayerErrorMessage(response.error || 'Unable to load linked bank accounts.');
        if (msg.toLowerCase().includes('reconnect') || msg.toLowerCase().includes('token')) {
          setIsBankConnected(false);
          setLinkedBankAccountsCount(0);
          setRequiresReconnect(true);
          setBankStatusMessage('Bank connection expired. Please reconnect your bank account.');
        } else {
          setIsBankConnected(false);
          setLinkedBankAccountsCount(0);
          setRequiresReconnect(false);
          setBankStatusMessage(msg);
        }
        trackTrueLayerEvent('truelayer_account_fetch_failed', {
          source,
          reconnectRequired: msg.toLowerCase().includes('reconnect') || msg.toLowerCase().includes('token'),
        });
        return;
      }

      const accounts = Array.isArray(response.data?.results) ? response.data.results : [];
      const normalizedAccounts = accounts.map((account: any) => ({
        accountId: String(account?.account_id || account?.id || ''),
        displayName: String(account?.display_name || account?.account_type || 'Bank account'),
        providerName: String(account?.provider?.display_name || account?.provider?.name || 'Provider'),
        currency: String(account?.currency || ''),
      }));
      setLinkedBankAccountsCount(accounts.length);
      const filteredAccounts = normalizedAccounts.filter(a => a.accountId);
      setLinkedBankAccounts(filteredAccounts);
      if (filteredAccounts.length > 0) {
        setSelectedLinkedAccountId(prev => prev && filteredAccounts.some(a => a.accountId === prev) ? prev : filteredAccounts[0].accountId);
      } else {
        setSelectedLinkedAccountId(null);
        setLinkedBalance(null);
        setLinkedTransactions([]);
      }
      setIsBankConnected(accounts.length > 0);
      if (accounts.length > 0) {
        setRequiresReconnect(false);
        setBankStatusMessage(`Connected (${accounts.length} linked account${accounts.length > 1 ? 's' : ''}).`);
      } else {
        setRequiresReconnect(false);
        setBankStatusMessage('No linked bank accounts yet. Connect and complete consent.');
      }
      trackTrueLayerEvent('truelayer_account_fetch_succeeded', {
        source,
        linkedAccountsCount: accounts.length,
      });
    } catch (err) {
      setIsBankConnected(false);
      setLinkedBankAccountsCount(0);
      setLinkedBankAccounts([]);
      setRequiresReconnect(false);
      setBankStatusMessage(mapTrueLayerErrorMessage('Unable to load linked bank accounts.'));
      trackTrueLayerEvent('truelayer_account_fetch_failed', { source, transportError: true });
    } finally {
      setIsRefreshingBankStatus(false);
    }
  };

  const refreshLinkedAccountBalance = async (accountId: string) => {
    if (!accountId || isLinkedBalanceLoading) return;
    setIsLinkedBalanceLoading(true);
    setLinkedBalanceError(null);
    try {
      const response = await apiService.get<{ results?: Array<any> }>(
        `/api/banking/truelayer/accounts/${accountId}/balance`
      );
      if (!response.success) {
        setLinkedBalanceError(mapTrueLayerErrorMessage(response.error || 'Unable to load bank balance.'));
        setLinkedBalance(null);
        return;
      }
      const first = Array.isArray(response.data?.results) ? response.data.results[0] : null;
      if (!first) {
        setLinkedBalanceError('No balance data available for this account.');
        setLinkedBalance(null);
        return;
      }
      setLinkedBalance({
        currency: String(first.currency || ''),
        available: typeof first.available === 'number' ? first.available : null,
        current: typeof first.current === 'number' ? first.current : null,
        overdraft: typeof first.overdraft === 'number' ? first.overdraft : null,
        updateTimestamp: first.update_timestamp ? String(first.update_timestamp) : null,
      });
    } catch (err) {
      setLinkedBalanceError(mapTrueLayerErrorMessage('Unable to load bank balance.'));
      setLinkedBalance(null);
    } finally {
      setIsLinkedBalanceLoading(false);
    }
  };

  const refreshLinkedAccountTransactions = async (accountId: string) => {
    if (!accountId || isLinkedTransactionsLoading) return;
    setIsLinkedTransactionsLoading(true);
    setLinkedTransactionsError(null);
    try {
      const response = await apiService.get<{ results?: Array<any> }>(
        `/api/banking/truelayer/accounts/${accountId}/transactions`
      );
      if (!response.success) {
        setLinkedTransactionsError(mapTrueLayerErrorMessage(response.error || 'Unable to load transactions.'));
        setLinkedTransactions([]);
        return;
      }
      const rows = Array.isArray(response.data?.results) ? response.data.results : [];
      const normalized = rows.map((tx: any, idx: number) => ({
        id: String(
          tx?.transaction_id ||
          tx?.id ||
          `${tx?.timestamp || tx?.booking_date_time || tx?.update_timestamp || idx}-${idx}`
        ),
        timestamp: String(tx?.timestamp || tx?.booking_date_time || tx?.update_timestamp || ''),
        description: String(tx?.description || tx?.merchant_name || tx?.reference || 'Transaction'),
        amount: Number(
          tx?.amount ??
          tx?.amount_in_minor ??
          tx?.value ??
          0
        ),
        currency: String(tx?.currency || ''),
      }));
      setLinkedTransactions(normalized);
    } catch (err) {
      setLinkedTransactionsError(mapTrueLayerErrorMessage('Unable to load transactions.'));
      setLinkedTransactions([]);
    } finally {
      setIsLinkedTransactionsLoading(false);
    }
  };

  useEffect(() => {
    if (!showTrueLayerBankPanel) return;
    if (!isAuthenticated || !profile?.id) return;
    refreshBankConnectionStatus('auto_refresh');
  }, [isAuthenticated, profile?.id, showTrueLayerBankPanel]);

  useEffect(() => {
    if (!showTrueLayerBankPanel) return;
    if (!selectedLinkedAccountId) return;
    refreshLinkedAccountBalance(selectedLinkedAccountId);
    refreshLinkedAccountTransactions(selectedLinkedAccountId);
  }, [selectedLinkedAccountId, showTrueLayerBankPanel]);

  const filteredLinkedTransactions = linkedTransactions.filter((tx) => {
    const text = linkedTxSearch.trim().toLowerCase();
    const matchesText = !text || tx.description.toLowerCase().includes(text);

    if (!linkedTxFromDate && !linkedTxToDate) {
      return matchesText;
    }

    const txDate = tx.timestamp ? new Date(tx.timestamp) : null;
    if (!txDate || Number.isNaN(txDate.getTime())) {
      return false;
    }

    const from = linkedTxFromDate ? new Date(`${linkedTxFromDate}T00:00:00`) : null;
    const to = linkedTxToDate ? new Date(`${linkedTxToDate}T23:59:59`) : null;
    const matchesFrom = !from || txDate >= from;
    const matchesTo = !to || txDate <= to;
    return matchesText && matchesFrom && matchesTo;
  });

  const handleCreateTrueLayerPayment = async () => {
    if (isCreatingTrueLayerPayment) return;
    setCreatePaymentError(null);
    setCreatedPaymentResult(null);
    setPaymentAuthorizationError(null);
    setPaymentAuthorizationUrl(null);
    setPaymentProviderSelectionRequired(false);
    setPaymentLifecycleStatus(null);
    setPaymentPollingMessage(null);

    const parsedAmount = Number(paymentAmountMajor);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setCreatePaymentError('Enter a valid amount greater than 0.');
      return;
    }
    if (!paymentCurrency || paymentCurrency.trim().length !== 3) {
      setCreatePaymentError('Enter a valid 3-letter currency (for example, EUR).');
      return;
    }
    if (!paymentBeneficiaryName.trim()) {
      setCreatePaymentError('Beneficiary account holder name is required.');
      return;
    }
    if (!paymentBeneficiaryIban.trim()) {
      setCreatePaymentError('Beneficiary IBAN is required.');
      return;
    }

    if (!isLikelyIban(paymentBeneficiaryIban.trim())) {
      setCreatePaymentError('Enter a valid IBAN (country code + check digits + account number).');
      return;
    }

    const referenceBase = paymentReference.trim() || 'pm';
    const generatedReference = buildShortPaymentReference(referenceBase);
    trackTrueLayerEvent('truelayer_payment_create_started', {
      currency: paymentCurrency.trim().toUpperCase(),
      hasCustomReference: !!paymentReference.trim(),
    });

    setIsCreatingTrueLayerPayment(true);
    try {
      const response = await apiService.post<any>('/api/banking/truelayer/payments', {
        amount: Number(parsedAmount.toFixed(2)),
        currency: paymentCurrency.trim().toUpperCase(),
        reference: generatedReference,
        payment_method: {
          type: 'bank_transfer',
          provider_selection: {
            type: 'user_selected',
          },
          beneficiary: {
            type: 'external_account',
            reference: generatedReference,
            account_holder_name: paymentBeneficiaryName.trim(),
            account_identifier: {
              type: 'iban',
              iban: paymentBeneficiaryIban.trim(),
            },
          },
        },
        user: {
          name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : 'Retail User',
          email: user?.email || 'retail.user@zogzila.com',
        },
      });

      if (!response.success) {
        setCreatePaymentError(mapTrueLayerErrorMessage(response.error || 'Failed to create payment.'));
        trackTrueLayerEvent('truelayer_payment_create_failed', {
          currency: paymentCurrency.trim().toUpperCase(),
        });
        return;
      }

      const paymentData = response.data || {};
      setCreatedPaymentResult({
        paymentId: String(paymentData.id || paymentData.payment_id || ''),
        resourceToken: paymentData.resource_token ? String(paymentData.resource_token) : undefined,
        status: paymentData.status ? String(paymentData.status) : undefined,
        reference: generatedReference,
        createdAtMs: Date.now(),
      });
      setPaymentLifecycleStatus(paymentData.status ? String(paymentData.status) : 'authorization_required');
      setPaymentReference(generatedReference);
      trackTrueLayerEvent('truelayer_payment_create_succeeded', {
        paymentId: String(paymentData.id || paymentData.payment_id || ''),
        paymentStatus: String(paymentData.status || 'authorization_required'),
        reference: generatedReference,
      });
    } catch (err) {
      setCreatePaymentError(mapTrueLayerErrorMessage('Failed to create payment.'));
      trackTrueLayerEvent('truelayer_payment_create_failed', {
        currency: paymentCurrency.trim().toUpperCase(),
        transportError: true,
      });
    } finally {
      setIsCreatingTrueLayerPayment(false);
    }
  };

  const stopPaymentStatusPolling = (message?: string) => {
    if (paymentPollingIntervalRef.current) {
      clearInterval(paymentPollingIntervalRef.current);
      paymentPollingIntervalRef.current = null;
    }
    paymentPollingStartedAtRef.current = null;
    setIsPollingPaymentStatus(false);
    if (message) {
      setPaymentPollingMessage(message);
    }
    if (paymentLifecycleStatus === 'settled' || paymentLifecycleStatus === 'failed') {
      trackTrueLayerEvent('truelayer_payment_status_finalized', {
        paymentId: createdPaymentResult?.paymentId || null,
        finalStatus: paymentLifecycleStatus,
      });
    }
  };

  const fetchPaymentStatus = async (paymentId: string, opts?: { silenceErrors?: boolean }) => {
    const response = await apiService.get<any>(`/api/banking/truelayer/payments/${paymentId}`);
    if (!response.success) {
      if (!opts?.silenceErrors) {
        setPaymentPollingMessage(mapTrueLayerErrorMessage(response.error || 'Failed to fetch payment status.'));
      }
      return null;
    }
    const status = String(
      response.data?.status ||
      response.data?.payment?.status ||
      response.data?.payment_status ||
      ''
    ).toLowerCase();
    if (status) {
      setPaymentLifecycleStatus(status);
    }
    return status || null;
  };

  const startPaymentStatusPolling = async (paymentId: string) => {
    if (!paymentId) return;

    stopPaymentStatusPolling();
    setIsPollingPaymentStatus(true);
    setPaymentPollingMessage('Tracking payment status...');
    paymentPollingStartedAtRef.current = Date.now();

    const FINAL_STATUSES = new Set(['settled', 'failed']);
    const TRANSITIONAL_STATUSES = new Set(['authorization_required', 'authorizing', 'authorized', 'executed']);
    const TIMEOUT_MS = 2 * 60 * 1000;

    const checkAndMaybeStop = async () => {
      const status = await fetchPaymentStatus(paymentId, { silenceErrors: true });
      if (!status) return;
      if (FINAL_STATUSES.has(status)) {
        stopPaymentStatusPolling(status === 'settled' ? 'Payment settled.' : 'Payment failed.');
        return;
      }
      if (!TRANSITIONAL_STATUSES.has(status)) {
        stopPaymentStatusPolling(`Payment status: ${status}`);
        return;
      }
      const startedAt = paymentPollingStartedAtRef.current;
      if (startedAt && Date.now() - startedAt >= TIMEOUT_MS) {
        stopPaymentStatusPolling('Payment status polling timed out. You can refresh status manually.');
      }
    };

    await checkAndMaybeStop();
    if (!paymentPollingStartedAtRef.current) {
      return;
    }

    paymentPollingIntervalRef.current = setInterval(() => {
      checkAndMaybeStop();
    }, 4000);
  };

  const handleStartPaymentAuthorization = async () => {
    if (isStartingPaymentAuthorization) return;
    if (!createdPaymentResult?.paymentId) {
      setPaymentAuthorizationError('Create a payment first.');
      return;
    }
    if (!createdPaymentResult?.resourceToken) {
      setPaymentAuthorizationError('Payment resource token is missing. Create a new payment.');
      return;
    }
    if (Date.now() - createdPaymentResult.createdAtMs > PAYMENT_CONTEXT_TTL_MS) {
      setPaymentAuthorizationError(
        mapTrueLayerErrorMessage('Authorization expired. Create a new payment to continue.')
      );
      return;
    }

    setIsStartingPaymentAuthorization(true);
    setPaymentAuthorizationError(null);
    setPaymentAuthorizationUrl(null);
    setPaymentProviderSelectionRequired(false);
    trackTrueLayerEvent('truelayer_payment_authorization_started', {
      paymentId: createdPaymentResult.paymentId,
    });

    try {
      const response = await apiService.post<any>(
        `/api/banking/truelayer/payments/${createdPaymentResult.paymentId}/authorization-flow`,
        {
          resource_token: createdPaymentResult.resourceToken,
          provider_selection: {
            type: 'user_selected',
          },
          redirect: {
            return_uri: getTrueLayerPaymentReturnUri(),
          },
        }
      );

      if (!response.success) {
        setPaymentAuthorizationError(mapTrueLayerErrorMessage(response.error || 'Failed to start payment authorization flow.'));
        trackTrueLayerEvent('truelayer_payment_authorization_failed', {
          paymentId: createdPaymentResult.paymentId,
        });
        return;
      }

      const loopResult = await runTrueLayerPaymentAuthorizationLoop(
        (path, body) => apiService.post<unknown>(path, body),
        createdPaymentResult.paymentId,
        createdPaymentResult.resourceToken,
        response.data,
        () => {},
        (redirectUri) => setPaymentAuthorizationUrl(redirectUri),
        { beneficiaryIban: paymentBeneficiaryIban.trim() }
      );

      if (loopResult.status === 'error') {
        setPaymentAuthorizationError(mapTrueLayerErrorMessage(loopResult.message));
        trackTrueLayerEvent('truelayer_payment_authorization_failed', {
          paymentId: createdPaymentResult.paymentId,
          missingRedirectAction: true,
        });
        return;
      }

      setPaymentProviderSelectionRequired(false);
      if (createdPaymentResult?.paymentId) {
        startPaymentStatusPolling(createdPaymentResult.paymentId);
      }
      trackTrueLayerEvent('truelayer_payment_authorization_redirect_ready', {
        paymentId: createdPaymentResult.paymentId,
      });
    } catch (err) {
      setPaymentAuthorizationError(mapTrueLayerErrorMessage('Failed to start payment authorization flow.'));
      trackTrueLayerEvent('truelayer_payment_authorization_failed', {
        paymentId: createdPaymentResult.paymentId,
        transportError: true,
      });
    } finally {
      setIsStartingPaymentAuthorization(false);
    }
  };

  useEffect(() => {
    return () => {
      if (paymentPollingIntervalRef.current) {
        clearInterval(paymentPollingIntervalRef.current);
      }
    };
  }, []);

  const paymentTimelineStages = ['authorization_required', 'authorizing', 'authorized', 'executed', 'settled', 'failed'];
  const currentTimelineIndex = paymentLifecycleStatus ? paymentTimelineStages.indexOf(paymentLifecycleStatus) : -1;
  const isPaymentContextStale = createdPaymentResult
    ? Date.now() - createdPaymentResult.createdAtMs > PAYMENT_CONTEXT_TTL_MS
    : false;

  const getTransactionIcon = (category: string) => {
    switch (category) {
      case 'Coffee': return '☕';
      case 'Subscription': return '🎬';
      case 'Transfer': return '💸';
      case 'Transport': return '🚗';
      case 'Food': return '🍔';
      case 'Shopping': return '🛒';
      case 'Income': return '💰';
      case 'Health': return '🏥';
      default: return '💳';
    }
  };

  // Add helper function to find savings account balance
  const getSavingsBalance = () => {
    const savingsAccount = dashboardData?.accounts?.find(account => account.accountType === 'Savings');
    return savingsAccount?.balance || 0;
  };

  // Get the primary currency
  const primaryCurrency = dashboardData?.primaryCurrency || 
    (dashboardData?.accounts ? getPrimaryCurrency(dashboardData.accounts) : 'USD');

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: branding.fontFamily 
      }}>
        Loading dashboard...
      </div>
    );
  }

  if (!dashboardData) {
    // If we have an error but no data, show error
    if (error && !error.includes('Network error occurred') && !error.includes('Session refresh failed')) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          fontFamily: branding.fontFamily,
          color: branding.errorColor 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, marginBottom: 16 }}>Error: {error}</div>
            <button 
              onClick={() => {
                hasFetchedData.current = false;
                isFetching.current = false;
                lastFetchTime.current = 0;
                dispatch(fetchDashboardData());
              }}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: branding.primaryColor,
                color: 'white',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    
    // If we're authenticated but have no data, show loading (fetch is driven only by useEffect — no dispatch during render)
    if (isAuthenticated && profile?.id) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          fontFamily: branding.fontFamily 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading dashboard...</div>
            <div style={{ color: '#666' }}>Please wait while we load your dashboard</div>
          </div>
        </div>
      );
    }
    
    // If not authenticated, redirect to login instead of showing "No data available"
    if (!isAuthenticated) {
      console.log('Dashboard: User not authenticated, redirecting to login');
      navigate('/login', { replace: true });
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          fontFamily: branding.fontFamily 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Redirecting to login...</div>
          </div>
        </div>
      );
    }
    
    // If authenticated but no profile data, show loading
    if (!profile?.id) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          fontFamily: branding.fontFamily 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading dashboard...</div>
            <div style={{ color: '#666' }}>Please wait while we load your profile</div>
          </div>
        </div>
      );
    }
    
    // Should not reach here, but just in case
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        fontFamily: branding.fontFamily 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // console.log('dashboardData', dashboardData);

  return (
    <div style={{
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '16px 0 80px 0' : '32px 0',
      margin: 0,
      flex: 1
    }}>
      <div style={{
        maxWidth: branding.viewport.mainMaxWidth,
        margin: isMobile ? '0 auto' : '0 auto',
        padding: isMobile ? '0 8px' : '0 24px',
      }}>
        {/* Page Title */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{
            color: branding.textColor,
            fontWeight: 700,
            fontSize: isMobile ? 24 : 28,
            margin: 0,
            marginBottom: 8,
          }}>Dashboard</h1>
          <div style={{ fontSize: isMobile ? 15 : 16, color: '#666' }}>
            Banking for everyone
          </div>
          {pendingUpgradeInfo?.hasPending && (
            <div
              style={{
                marginTop: 16,
                padding: '12px 16px',
                borderRadius: 12,
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                fontSize: 14,
                color: '#1e3a5f',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Subscription upgrade in progress</div>
              <div>{pendingUpgradeInfo.message || 'We are processing your upgrade.'}</div>
              {pendingUpgradeInfo.status === 'PENDING_CRYPTO_VERIFICATION' &&
                String(pendingUpgradeInfo.paymentMethod || '').toUpperCase() === 'CRYPTO' && (
                <div style={{ fontSize: 12, color: '#475569', marginTop: 10, lineHeight: 1.55 }}>
                  Your payment is in the verification queue. A staff member with{' '}
                  <strong>Manage Subscriptions</strong> checks the transaction on-chain, then your plan is activated
                  (an admin UI for this is being added; approvals can also be done via the operations API today).
                </div>
              )}
              {pendingUpgradeInfo.status === 'AWAITING_PAYMENT' &&
                pendingUpgradeInfo.paymentMethod !== 'CRYPTO' &&
                pendingUpgradeInfo.checkoutUrl && (
                <button
                  type="button"
                  onClick={() =>
                    window.open(pendingUpgradeInfo.checkoutUrl as string, '_blank', 'noopener,noreferrer')
                  }
                  style={{
                    marginTop: 10,
                    padding: '8px 16px',
                    borderRadius: 999,
                    border: 'none',
                    background: '#086afb',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Open secure payment (Stripe)
                </button>
              )}
              {pendingUpgradeInfo.status === 'AWAITING_PAYMENT' &&
                pendingUpgradeInfo.paymentMethod === 'CRYPTO' && (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                    Complete your crypto payment using either option below.
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <div><strong>Network:</strong> {String((pendingUpgradeInfo.blockchain || (pendingUpgradeInfo as any).cryptoBlockchain || '-') as string).toUpperCase()}</div>
                    <div><strong>Symbol:</strong> {pendingUpgradeInfo.symbol || (pendingUpgradeInfo as any).cryptoSymbol || '-'}</div>
                    <div><strong>Amount:</strong> {pendingUpgradeInfo.amount ?? (pendingUpgradeInfo as any).amountMajor ?? '-'} {pendingUpgradeInfo.currency || (pendingUpgradeInfo as any).amountCurrency || ''}</div>
                    <div style={{ wordBreak: 'break-all' }}>
                      <strong>Destination wallet (pay to):</strong>{' '}
                      {pendingUpgradeInfo.destinationAddress || (pendingUpgradeInfo as any).cryptoDestinationAddress || '-'}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 16,
                      marginTop: 12,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        flex: '1 1 200px',
                        maxWidth: 280,
                        padding: 10,
                        background: '#fff',
                        borderRadius: 10,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                        Pay to (deposit address)
                      </div>
                      {subscriptionDestQrDataUrl && (
                        <div style={{ textAlign: 'center', marginBottom: 8 }}>
                          <img src={subscriptionDestQrDataUrl} alt="QR code for deposit address" style={{ width: 140, height: 140 }} />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const a =
                            pendingUpgradeInfo.destinationAddress ||
                            (pendingUpgradeInfo as any).cryptoDestinationAddress ||
                            '';
                          if (a) void navigator.clipboard.writeText(String(a));
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid #cbd5e1',
                          background: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: '#263143',
                        }}
                      >
                        Copy deposit address
                      </button>
                    </div>

                    <div
                      style={{
                        flex: '1 1 200px',
                        maxWidth: 280,
                        padding: 10,
                        background: '#fff',
                        borderRadius: 10,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                        Your Zogzila wallet (pay from)
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.45 }}>
                        Used when you tap &quot;Pay with my Crypto wallet&quot;. External wallets (MetaMask, etc.) use
                        your own address — scan or copy the deposit QR on the left.
                      </div>
                      {subscriptionWalletLoading && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>Loading your wallet…</div>
                      )}
                      {subscriptionWalletLoadError && (
                        <div style={{ fontSize: 12, color: '#b91c1c' }}>{subscriptionWalletLoadError}</div>
                      )}
                      {!subscriptionWalletLoading && !subscriptionWalletLoadError && subscriptionPayFromWallet && (
                        <>
                          {subscriptionUserWalletQrDataUrl && (
                            <div style={{ textAlign: 'center', marginBottom: 8 }}>
                              <img
                                src={subscriptionUserWalletQrDataUrl}
                                alt="QR code for your Zogzila wallet address"
                                style={{ width: 140, height: 140 }}
                              />
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: 11,
                              fontFamily: 'ui-monospace, monospace',
                              wordBreak: 'break-all',
                              color: '#334155',
                              marginBottom: 8,
                            }}
                          >
                            {subscriptionPayFromWallet.address}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (subscriptionPayFromWallet.address) {
                                void navigator.clipboard.writeText(subscriptionPayFromWallet.address);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '6px 10px',
                              borderRadius: 8,
                              border: '1px solid #cbd5e1',
                              background: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              color: '#263143',
                            }}
                          >
                            Copy your wallet address
                          </button>
                        </>
                      )}
                      {!subscriptionWalletLoading &&
                        !subscriptionWalletLoadError &&
                        !subscriptionPayFromWallet && (
                        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                          No Zogzila wallet on this network yet.{' '}
                          <button
                            type="button"
                            onClick={() => navigate('/crypto')}
                            style={{
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              color: '#086afb',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            Open Crypto
                          </button>{' '}
                          to create or import one.
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: '#64748b',
                      marginTop: 10,
                      lineHeight: 1.55,
                      paddingTop: 10,
                      borderTop: '1px solid #e2e8f0',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#334155' }}>External wallet</span>
                    {' — '}
                    Paying from MetaMask, Ledger, an exchange, or any other app? Send the correct token on the network
                    above to the destination address, then paste the transaction hash and tap Submit.
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.55 }}>
                    <span style={{ fontWeight: 600, color: '#334155' }}>Zogzila Crypto wallet</span>
                    {' — '}
                    If you created or imported a wallet in the Crypto section, you can use Pay with my Crypto wallet
                    instead (no hash needed from you if the send succeeds in-app).
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => void payUpgradeWithIntegratedCryptoWallet()}
                      disabled={cryptoWalletPayBusy}
                      style={{
                        padding: '9px 14px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#086afb',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: cryptoWalletPayBusy ? 'not-allowed' : 'pointer',
                        opacity: cryptoWalletPayBusy ? 0.7 : 1,
                      }}
                    >
                      {cryptoWalletPayBusy ? 'Paying…' : 'Pay with my Crypto wallet'}
                    </button>
                    <input
                      type="text"
                      placeholder="Paste transaction hash"
                      value={cryptoTxHash}
                      onChange={(e) => setCryptoTxHash(e.target.value)}
                      style={{
                        flex: 1,
                        minWidth: 240,
                        padding: '9px 12px',
                        borderRadius: 8,
                        border: '1px solid #cbd5e1',
                        fontSize: 13
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => void submitCryptoPaymentProof()}
                      disabled={cryptoSubmitBusy || !cryptoTxHash.trim()}
                      style={{
                        padding: '9px 14px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#0dca9d',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: cryptoSubmitBusy || !cryptoTxHash.trim() ? 'not-allowed' : 'pointer',
                        opacity: cryptoSubmitBusy || !cryptoTxHash.trim() ? 0.7 : 1,
                      }}
                    >
                      {cryptoSubmitBusy ? 'Submitting…' : 'Submit tx hash'}
                    </button>
                  </div>
                  {cryptoSubmitError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{cryptoSubmitError}</div>
                  )}
                  {cryptoWalletPayError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{cryptoWalletPayError}</div>
                  )}
                </div>
              )}
            </div>
          )}
          {showTrueLayerBankPanel && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={handleConnectBankAccount}
                  disabled={isConnectingBank}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#086afb',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: isConnectingBank ? 'not-allowed' : 'pointer',
                    opacity: isConnectingBank ? 0.75 : 1
                  }}
                >
                  {isConnectingBank
                    ? 'Opening bank consent...'
                    : requiresReconnect
                      ? 'Reconnect bank account'
                      : 'Connect bank account'}
                </button>
                <button
                  onClick={() => refreshBankConnectionStatus('consent_return')}
                  disabled={isRefreshingBankStatus}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: '1px solid #d5d9e3',
                    background: '#fff',
                    color: '#263143',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: isRefreshingBankStatus ? 'not-allowed' : 'pointer',
                    opacity: isRefreshingBankStatus ? 0.7 : 1
                  }}
                >
                  {isRefreshingBankStatus ? 'Refreshing...' : "I've completed consent"}
                </button>
              </div>
              {isBankConnected !== null && (
                <div style={{ marginTop: 8, fontSize: 13, color: isBankConnected ? '#0dca9d' : '#9ca3af' }}>
                  {isBankConnected
                    ? `TrueLayer connected (${linkedBankAccountsCount} account${linkedBankAccountsCount > 1 ? 's' : ''})`
                    : 'TrueLayer not connected'}
                </div>
              )}
              {requiresReconnect && (
                <div style={{ color: '#ff7171', marginTop: 6, fontSize: 13 }}>
                  Connection expired or revoked. Use "Reconnect bank account" to restore bank data access.
                </div>
              )}
              {bankStatusMessage && (
                <div style={{ color: isBankConnected ? '#0dca9d' : '#ff7171', marginTop: 6, fontSize: 13 }}>
                  {bankStatusMessage}
                </div>
              )}
              {connectBankError && (
                <div style={{ color: '#ff7171', marginTop: 8, fontSize: 13 }}>
                  {connectBankError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Token Refresh Test Component - Remove in production */}
        {/* <TokenRefreshTest /> */}
      </div>
      <div style={{
        maxWidth: branding.viewport.mainMaxWidth,
        margin: isMobile ? '0 auto' : '0 auto',
        padding: isMobile ? '0 8px' : '0 24px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 24
      }}>
        {/* Left Column (Main Cards) */}
        <div
          style={{
            flex: 2,
            display: !isMobile && !hasDesktopLeftContent ? 'none' : 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {/* Real Balance Card */}
          {userPermissions.includes('VIEW_RETAIL_DASHBOARD_BALANCE') && (
            <div style={{
            background: '#263143',
            borderRadius: 16,
            padding: isMobile ? 20 : 32,
            color: '#fff',
            marginBottom: 0,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 500, marginBottom: 8 }}>Real Balance</div>
            <div style={{ fontSize: isMobile ? 32 : 40, fontWeight: 700, marginBottom: 12 }}>
              {formatAmount(dashboardData.totalBalance, primaryCurrency)}
            </div>
            {/* Divider line */}
            <div style={{ borderBottom: '1px solid #dbe2ef', margin: '12px 0' }} />
            {/* Balances row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: isMobile ? 14 : 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Current:</span>
                <span style={{ fontWeight: 600 }}>
                  {formatAmount(dashboardData.currentBalance, primaryCurrency)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Blocked:</span>
                <span style={{ fontWeight: 600 }}>
                  {formatAmount(dashboardData.blockedBalance, primaryCurrency)}
                </span>
              </div>
            </div>
          </div>
          )}

          {/* Subscription Status Card (MOBILE ONLY) */}
          {isMobile && subscriptionStatus && (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 20,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>My Subscription</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, color: '#555' }}>Plan</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#263143' }}>
                    {subscriptionStatus.planName || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, color: '#555' }}>Status</span>
                  <span style={{
                    padding: '3px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    background:
                      subscriptionStatus.status === 'ACTIVE' ? '#d1fae5' :
                      subscriptionStatus.status === 'GRACE_PERIOD' ? '#fef9c3' :
                      subscriptionStatus.status === 'SUSPENDED' ? '#ffedd5' :
                      subscriptionStatus.status === 'EXPIRED' ? '#fee2e2' :
                      subscriptionStatus.status === 'CANCELLED' ? '#f3f4f6' : '#f3f4f6',
                    color:
                      subscriptionStatus.status === 'ACTIVE' ? '#065f46' :
                      subscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' :
                      subscriptionStatus.status === 'SUSPENDED' ? '#9a3412' :
                      subscriptionStatus.status === 'EXPIRED' ? '#991b1b' :
                      subscriptionStatus.status === 'CANCELLED' ? '#6b7280' : '#6b7280',
                  }}>
                    {subscriptionStatus.status === 'NONE' ? 'No Subscription' :
                     subscriptionStatus.status === 'GRACE_PERIOD' ? 'Grace Period' :
                     subscriptionStatus.status === 'CANCELLED' ? 'Cancelled' :
                     subscriptionStatus.status || 'Unknown'}
                  </span>
                </div>
                {subscriptionStatus.expiresAt && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: '#555' }}>Expires</span>
                    <span style={{ fontWeight: 600, fontSize: 14, color: subscriptionStatus.status === 'EXPIRED' ? '#991b1b' : '#263143' }}>
                      {new Date(subscriptionStatus.expiresAt).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {(subscriptionStatus.status === 'NONE' ||
                  subscriptionStatus.status === 'EXPIRED' ||
                  subscriptionStatus.status === 'SUSPENDED' ||
                  subscriptionStatus.status === 'CANCELLED') &&
                  subscriptionStatus.message && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '10px 14px',
                      background: '#fff7ed',
                      borderRadius: 8,
                      borderLeft: '3px solid #f59e0b',
                      fontSize: 13,
                      color: '#92400e',
                      lineHeight: 1.5,
                    }}
                  >
                    {subscriptionStatus.message}
                  </div>
                )}
                {renewError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#b91c1c' }}>{renewError}</div>
                )}
                {subscriptionRenewEligible && (
                  <div style={{ marginTop: 8, marginBottom: 2 }}>
                    <label style={{ fontSize: 12, color: '#64748b', marginRight: 8 }}>Payment:</label>
                    <select
                      value={subscriptionPaymentMethod}
                      onChange={(e) => setSubscriptionPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
                      style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    >
                      {renewalAllowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Stripe</option>}
                      {renewalAllowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto/Stablecoin</option>}
                    </select>
                  </div>
                )}
                {subscriptionRenewEligible && isCryptoPaymentMethod && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <input
                      value={cryptoBlockchain}
                      onChange={(e) => setCryptoBlockchain(e.target.value)}
                      placeholder="network"
                      style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    />
                    <input
                      value={cryptoSymbol}
                      onChange={(e) => setCryptoSymbol(e.target.value.toUpperCase())}
                      placeholder="symbol"
                      style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    />
                  </div>
                )}
                {subscriptionRenewEligible && (
                  <button
                    type="button"
                    disabled={renewBusy}
                    onClick={() => void startRenewalFlow()}
                    style={{
                      marginTop: 8,
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#0dca9d',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: renewBusy ? 'not-allowed' : 'pointer',
                      opacity: renewBusy ? 0.7 : 1,
                    }}
                  >
                    {renewBusy ? 'Please wait…' : 'Renew subscription'}
                  </button>
                )}
                {subscriptionUpgradeEligible && !pendingUpgradeInfo?.hasPending && (
                  <button
                    type="button"
                    onClick={() => void openUpgradeModal()}
                    style={{
                      marginTop: 8,
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#086afb',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Upgrade plan
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions Card (MOBILE ONLY) */}
          {isMobile && userPermissions.includes('VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS') && (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 16,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>Quick Actions</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                {/* Send Money */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={() => navigate('/payments')} style={{ background: '#0a84ff', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Send">✈️</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Send Money</span>
                </div>
                {/* Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={() => navigate('/cards')} style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Cards">💳</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Cards</span>
                </div>
                {/* Invest (hide for child accounts, and only show if profile is loaded) */}
                {profile && !isChildAccount && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                    <button onClick={handleAssetsClick} style={{ background: '#f1c40f', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                      <span role="img" aria-label="Invest">📈</span>
                    </button>
                    <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Invest</span>
                  </div>
                )}
                {/* Trading Bots (hide for child accounts) */}
                {!isChildAccount && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                    <button onClick={handleTradingBotsClick} style={{ background: '#9b59b6', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                      <span role="img" aria-label="Trading Bots">🤖</span>
                    </button>
                    <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Trading Bots</span>
                  </div>
                )}
                {/* Savings */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={handleSavingsClick} style={{ background: '#8e44ad', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Savings">🐷</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Savings</span>
                </div>
              </div>
            </div>
          )}

          {/* Linked Bank Accounts Card (TrueLayer) */}
          {showTrueLayerBankPanel && (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: isMobile ? 16 : 24,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: isMobile ? 16 : 18, marginBottom: 12 }}>
                Linked Bank Accounts (TrueLayer)
              </div>

              {isRefreshingBankStatus && (
                <div style={{ color: '#6b7280', fontSize: 14 }}>Refreshing linked accounts...</div>
              )}

              {!isRefreshingBankStatus && linkedBankAccounts.length === 0 && (
                <div style={{ color: '#6b7280', fontSize: 14 }}>
                  No linked accounts yet. Connect and complete consent to see your bank accounts.
                </div>
              )}

              {!isRefreshingBankStatus && linkedBankAccounts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {linkedBankAccounts.map((account) => (
                    <div
                      key={account.accountId}
                      onClick={() => setSelectedLinkedAccountId(account.accountId)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: selectedLinkedAccountId === account.accountId ? '1px solid #086afb' : '1px solid #eef1f5',
                        borderRadius: 10,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: selectedLinkedAccountId === account.accountId ? '#f0f6ff' : '#fff',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 14 }}>
                          {account.displayName}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                          {account.providerName} • {account.accountId.slice(-6)}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#263143', fontSize: 13 }}>
                        {account.currency || 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isRefreshingBankStatus && selectedLinkedAccountId && (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef1f5', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Linked Account Balance</div>
                    <button
                      onClick={() => refreshLinkedAccountBalance(selectedLinkedAccountId)}
                      disabled={isLinkedBalanceLoading}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        background: '#fff',
                        color: '#263143',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: isLinkedBalanceLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isLinkedBalanceLoading ? 'Refreshing...' : 'Refresh balance'}
                    </button>
                  </div>

                  {isLinkedBalanceLoading && (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>Loading balance...</div>
                  )}

                  {!isLinkedBalanceLoading && linkedBalanceError && (
                    <div style={{ color: '#ff7171', fontSize: 13 }}>{linkedBalanceError}</div>
                  )}

                  {!isLinkedBalanceLoading && !linkedBalanceError && linkedBalance && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#374151' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Current</span>
                        <strong>{formatAmount(linkedBalance.current || 0, linkedBalance.currency || 'EUR')}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Available</span>
                        <strong>{formatAmount(linkedBalance.available || 0, linkedBalance.currency || 'EUR')}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Overdraft</span>
                        <strong>{formatAmount(linkedBalance.overdraft || 0, linkedBalance.currency || 'EUR')}</strong>
                      </div>
                      {linkedBalance.updateTimestamp && (
                        <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
                          Updated: {new Date(linkedBalance.updateTimestamp).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isRefreshingBankStatus && selectedLinkedAccountId && (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef1f5', paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937', marginBottom: 8 }}>
                    Linked Account Transactions
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <input
                      value={linkedTxSearch}
                      onChange={(e) => setLinkedTxSearch(e.target.value)}
                      placeholder="Search description"
                      style={{
                        flex: 1,
                        minWidth: 170,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        fontSize: 12,
                      }}
                    />
                    <input
                      type="date"
                      value={linkedTxFromDate}
                      onChange={(e) => setLinkedTxFromDate(e.target.value)}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        fontSize: 12,
                      }}
                    />
                    <input
                      type="date"
                      value={linkedTxToDate}
                      onChange={(e) => setLinkedTxToDate(e.target.value)}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        fontSize: 12,
                      }}
                    />
                  </div>

                  {isLinkedTransactionsLoading && (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>Loading transactions...</div>
                  )}

                  {!isLinkedTransactionsLoading && linkedTransactionsError && (
                    <div style={{ color: '#ff7171', fontSize: 13 }}>{linkedTransactionsError}</div>
                  )}

                  {!isLinkedTransactionsLoading && !linkedTransactionsError && filteredLinkedTransactions.length === 0 && (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>No transactions found for current filters.</div>
                  )}

                  {!isLinkedTransactionsLoading && !linkedTransactionsError && filteredLinkedTransactions.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
                      {filteredLinkedTransactions.slice(0, 30).map((tx) => (
                        <div
                          key={tx.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #eef1f5',
                            borderRadius: 8,
                            padding: '8px 10px',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {tx.description}
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>
                              {tx.timestamp ? new Date(tx.timestamp).toLocaleString() : 'N/A'}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: tx.amount < 0 ? '#dc2626' : '#16a34a' }}>
                            {tx.amount < 0 ? '-' : '+'}
                            {formatAmount(Math.abs(tx.amount), tx.currency || linkedBalance?.currency || 'EUR')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!isRefreshingBankStatus &&
                linkedBankAccounts.length > 0 &&
                featureFlags.retail.enableTrueLayerDashboardPaymentsWidget && (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef1f5', paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937', marginBottom: 8 }}>
                    Create Payment (TrueLayer)
                  </div>
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                    <input
                      value={paymentAmountMajor}
                      onChange={(e) => setPaymentAmountMajor(e.target.value)}
                      placeholder="Amount (e.g. 10.00)"
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d5d9e3', fontSize: 12 }}
                    />
                    <input
                      value={paymentCurrency}
                      onChange={(e) => setPaymentCurrency(e.target.value.toUpperCase())}
                      placeholder="Currency (EUR)"
                      maxLength={3}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d5d9e3', fontSize: 12 }}
                    />
                    <input
                      value={paymentBeneficiaryName}
                      onChange={(e) => setPaymentBeneficiaryName(e.target.value)}
                      placeholder="Beneficiary name"
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d5d9e3', fontSize: 12 }}
                    />
                    <input
                      value={paymentBeneficiaryIban}
                      onChange={(e) => setPaymentBeneficiaryIban(e.target.value)}
                      placeholder="Beneficiary IBAN"
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d5d9e3', fontSize: 12 }}
                    />
                    <input
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="Reference (optional, auto-generated if empty)"
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d5d9e3', fontSize: 12, gridColumn: isMobile ? 'auto' : '1 / span 2' }}
                    />
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleCreateTrueLayerPayment}
                      disabled={isCreatingTrueLayerPayment}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: 'none',
                        background: '#086afb',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: isCreatingTrueLayerPayment ? 'not-allowed' : 'pointer',
                        opacity: isCreatingTrueLayerPayment ? 0.75 : 1,
                      }}
                    >
                      {isCreatingTrueLayerPayment ? 'Creating payment...' : 'Create payment'}
                    </button>
                    <button
                      onClick={handleStartPaymentAuthorization}
                      disabled={
                        isStartingPaymentAuthorization ||
                        !createdPaymentResult?.paymentId ||
                        isPaymentContextStale
                      }
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        background: '#fff',
                        color: '#263143',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: (
                          isStartingPaymentAuthorization ||
                          !createdPaymentResult?.paymentId ||
                          isPaymentContextStale
                        ) ? 'not-allowed' : 'pointer',
                        opacity: (
                          isStartingPaymentAuthorization ||
                          !createdPaymentResult?.paymentId ||
                          isPaymentContextStale
                        ) ? 0.75 : 1,
                      }}
                    >
                      {isStartingPaymentAuthorization ? 'Starting authorization...' : 'Start authorization'}
                    </button>
                    <button
                      onClick={() => createdPaymentResult?.paymentId && fetchPaymentStatus(createdPaymentResult.paymentId)}
                      disabled={!createdPaymentResult?.paymentId || isPollingPaymentStatus}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid #d5d9e3',
                        background: '#fff',
                        color: '#263143',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: (!createdPaymentResult?.paymentId || isPollingPaymentStatus) ? 'not-allowed' : 'pointer',
                        opacity: (!createdPaymentResult?.paymentId || isPollingPaymentStatus) ? 0.75 : 1,
                      }}
                    >
                      Refresh status
                    </button>
                  </div>
                  {createPaymentError && (
                    <div style={{ color: '#ff7171', marginTop: 8, fontSize: 12 }}>{createPaymentError}</div>
                  )}
                  {paymentAuthorizationError && (
                    <div style={{ color: '#ff7171', marginTop: 8, fontSize: 12 }}>{paymentAuthorizationError}</div>
                  )}
                  {createdPaymentResult?.paymentId && (
                    <div style={{ color: '#0dca9d', marginTop: 8, fontSize: 12 }}>
                      Payment created: {createdPaymentResult.paymentId}
                      {createdPaymentResult.status ? ` (${createdPaymentResult.status})` : ''}
                      {createdPaymentResult.reference ? ` • Ref: ${createdPaymentResult.reference}` : ''}
                    </div>
                  )}
                  {createdPaymentResult?.paymentId && (
                    <div style={{ color: isPaymentContextStale ? '#b45309' : '#4b5563', marginTop: 4, fontSize: 12 }}>
                      Context age: {Math.floor((Date.now() - createdPaymentResult.createdAtMs) / 1000)}s
                      {isPaymentContextStale ? ' • Expired: create a new payment before authorization.' : ''}
                    </div>
                  )}
                  {createdPaymentResult?.paymentId && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#4b5563' }}>
                      Status tracking: {isPollingPaymentStatus ? 'live' : 'idle'}
                      {paymentPollingMessage ? ` • ${paymentPollingMessage}` : ''}
                    </div>
                  )}
                  {createdPaymentResult?.paymentId && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {paymentTimelineStages.map((stage, index) => {
                        const isFailedStage = paymentLifecycleStatus === 'failed' && stage === 'failed';
                        const isComplete = currentTimelineIndex >= index && !isFailedStage;
                        const isActive = paymentLifecycleStatus === stage;
                        const bg = isFailedStage
                          ? '#fee2e2'
                          : isComplete
                            ? '#dcfce7'
                            : isActive
                              ? '#dbeafe'
                              : '#f3f4f6';
                        const color = isFailedStage
                          ? '#b91c1c'
                          : isComplete
                            ? '#166534'
                            : isActive
                              ? '#1d4ed8'
                              : '#6b7280';
                        return (
                          <span
                            key={stage}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 12,
                              background: bg,
                              color,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}
                          >
                            {stage.replace('_', ' ')}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {paymentProviderSelectionRequired && (
                    <div style={{ color: '#92400e', marginTop: 8, fontSize: 12 }}>
                      Provider selection is required. Continue from the authorization prompt to choose your bank.
                    </div>
                  )}
                  {paymentAuthorizationUrl && (
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      <a href={paymentAuthorizationUrl} target="_blank" rel="noreferrer" style={{ color: '#086afb', textDecoration: 'none' }}>
                        Open authorization page
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Accounts Card */}
          {userPermissions.includes('VIEW_RETAIL_DASHBOARD_ACCOUNTS') && (
            <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: isMobile ? 16 : 24,
            marginBottom: 0,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontWeight: 600, fontSize: isMobile ? 16 : 18, marginBottom: 16 }}>Accounts</div>
            {dashboardData.accounts.map((account) => (
              <div 
                key={account.id} 
                onClick={() => navigate(`/account?accountId=${account.id}`)}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '8px 0', 
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{account.accountType.charAt(0) + account.accountType.slice(1).toLowerCase()}</div>
                  <div style={{ color: '#888', fontSize: 14 }}>****{account.accountNumber.slice(-4)}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>
                  {formatAmount(account.balance, account.currency)}
                </div>
              </div>
            ))}
          </div>
          )}

          {/* Recent Transactions Card */}
          {userPermissions.includes('VIEW_RETAIL_DASHBOARD_TRANSACTIONS') && (
            <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: isMobile ? 16 : 24,
            marginBottom: 0,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: isMobile ? 16 : 18 }}>Recent Transactions</div>
              <a href="/transactions" style={{ color: branding.primaryColor, textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>View All</a>
            </div>
            {dashboardData.recentTransactions.slice(0, isMobile ? 4 : 7).map((tx) => (
              <div
                key={tx.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '10px 0',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  flexDirection: 'column'
                }}
                onClick={() => setExpandedTxId(expandedTxId === tx.id ? null : tx.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <span style={{ fontSize: 28 }}>{getTransactionIcon(tx.category)}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{tx.description}</div>
                    <div style={{ color: '#888', fontSize: 13 }}>
                      {new Date(tx.transactionDate).toLocaleDateString()}
                      <span style={{ marginLeft: 8, background: '#f5f5f5', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
                        {tx.category}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: tx.amount < 0 ? '#e74c3c' : '#27ae60', fontSize: 16 }}>
                    {tx.amount < 0 ? '-' : '+'}{formatAmount(Math.abs(tx.amount), tx.currency)}
                  </div>
                </div>
                {expandedTxId === tx.id && (
                  <div style={{ width: '100%', background: '#f8fafc', borderRadius: 8, marginTop: 8, padding: 12, fontSize: 14 }}>
                    <div><strong>Status:</strong> {tx.status}</div>
                    <div><strong>Type:</strong> {tx.type}</div>
                    <div><strong>Date:</strong> {new Date(tx.transactionDate).toLocaleString()}</div>
                    {/* Add more fields as needed */}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}

          {/* Multi-Signature Wallets Card (MOBILE ONLY) */}
          {isMobile && userPermissions.includes('VIEW_RETAIL_DASHBOARD_MULTISIG_WALLETS') && (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: isMobile ? 16 : 24,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: isMobile ? 16 : 18, marginBottom: 16 }}>Multi-Signature Wallets</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div 
                  onClick={() => navigate('/multi-signature-wallets')}
                  style={{ 
                    flex: 1, 
                    minWidth: 100, 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🔐</div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                    My Signatory Wallets
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                    {dashboardData?.pendingMultiSigApprovals ? `${dashboardData.pendingMultiSigApprovals} pending` : 'View my wallets'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Right Column (DESKTOP ONLY) */}
        {!isMobile && (
          <div
            style={{
              flex: !hasDesktopLeftContent ? 1 : 1,
              width: !hasDesktopLeftContent ? '100%' : undefined,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              marginTop: 0,
            }}
          >
            {/* Quick Actions Card (DESKTOP ONLY) */}
            {userPermissions.includes('VIEW_RETAIL_DASHBOARD_QUICK_ACTIONS') && (
              <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Quick Actions</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {/* Send Money */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={() => navigate('/payments')} style={{ background: '#0a84ff', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Send">✈️</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Send Money</span>
                </div>
                {/* Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={() => navigate('/cards')} style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Cards">💳</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Cards</span>
                </div>
                {/* Invest */}
                {profile && !isChildAccount && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                    <button onClick={handleAssetsClick} style={{ background: '#f1c40f', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                      <span role="img" aria-label="Invest">📈</span>
                    </button>
                    <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Invest</span>
                  </div>
                )}
                {/* Savings */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                  <button onClick={handleSavingsClick} style={{ background: '#8e44ad', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 8, cursor: 'pointer' }}>
                    <span role="img" aria-label="Savings">🐷</span>
                  </button>
                  <span style={{ fontSize: 15, color: '#222', fontWeight: 500, textAlign: 'center' }}>Savings</span>
                </div>
              </div>
            </div>
            )}
            {/* Investments Card - Show only if user has specific investment permissions */}
            {availableInvestmentFeatures.length > 0 && (
              <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Investments</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {/* Crypto (hide for child accounts) */}
                {!isChildAccount && availableInvestmentFeatures.includes('crypto') && (
                  <div 
                    onClick={handleCryptoClick}
                    style={{ 
                      flex: 1, 
                      minWidth: 100, 
                      background: '#f8fafc', 
                      borderRadius: 12, 
                      padding: 16, 
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>₿</div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      Crypto
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                      {formatAmount(0, dashboardData.primaryCurrency)}
                    </div>
                  </div>
                )}
                {/* Assets (hide for child accounts) */}
                {!isChildAccount && availableInvestmentFeatures.includes('assets') && (
                  <div 
                    onClick={handleAssetsClick}
                    style={{ 
                      flex: 1, 
                      minWidth: 100, 
                      background: '#f8fafc', 
                      borderRadius: 12, 
                      padding: 16, 
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>📈</div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      Assets
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                      {formatAmount(dashboardData.totalAssetsValue, dashboardData.primaryCurrency)}
                    </div>
                  </div>
                )}
                {/* Trading Bots (hide for child accounts) */}
                {!isChildAccount && availableInvestmentFeatures.includes('tradingBots') && (
                  <div 
                    onClick={handleTradingBotsClick}
                    style={{ 
                      flex: 1, 
                      minWidth: 100, 
                      background: '#f8fafc', 
                      borderRadius: 12, 
                      padding: 16, 
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>🤖</div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      Trading Bots
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                      {tradingBotBalance ? 
                        `${formatAmount(tradingBotBalance.available, dashboardData.primaryCurrency)} available` :
                        'View bots'
                      }
                    </div>
                    {tradingBotBalance && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        {formatAmount(tradingBotBalance.allocated, dashboardData.primaryCurrency)} allocated
                      </div>
                    )}
                  </div>
                )}
                {/* Savings */}
                {availableInvestmentFeatures.includes('savings') && (
                <div 
                  onClick={handleSavingsClick}
                  style={{ 
                    flex: 1, 
                    minWidth: 100, 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🐷</div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                    Savings
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                    {formatAmount(getSavingsBalance(), dashboardData.primaryCurrency)}
                  </div>
                </div>
                )}
              </div>
            </div>
            )}

            {/* Subscription Status Card */}
            {subscriptionStatus && (
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                marginBottom: 0,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>My Subscription</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Plan & Status Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: '#555' }}>Plan</span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#263143' }}>
                      {subscriptionStatus.planName || 'N/A'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: '#555' }}>Status</span>
                    <span style={{
                      padding: '3px 12px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      background:
                        subscriptionStatus.status === 'ACTIVE' ? '#d1fae5' :
                        subscriptionStatus.status === 'GRACE_PERIOD' ? '#fef9c3' :
                        subscriptionStatus.status === 'SUSPENDED' ? '#ffedd5' :
                        subscriptionStatus.status === 'EXPIRED' ? '#fee2e2' :
                        subscriptionStatus.status === 'CANCELLED' ? '#f3f4f6' : '#f3f4f6',
                      color:
                        subscriptionStatus.status === 'ACTIVE' ? '#065f46' :
                        subscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' :
                        subscriptionStatus.status === 'SUSPENDED' ? '#9a3412' :
                        subscriptionStatus.status === 'EXPIRED' ? '#991b1b' :
                        subscriptionStatus.status === 'CANCELLED' ? '#6b7280' : '#6b7280',
                    }}>
                      {subscriptionStatus.status === 'NONE' ? 'No Subscription' :
                       subscriptionStatus.status === 'GRACE_PERIOD' ? 'Grace Period' :
                       subscriptionStatus.status === 'CANCELLED' ? 'Cancelled' :
                       subscriptionStatus.status || 'Unknown'}
                    </span>
                  </div>
                  {/* Expiry */}
                  {subscriptionStatus.expiresAt && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, color: '#555' }}>Expires</span>
                      <span style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: subscriptionStatus.status === 'EXPIRED' ? '#991b1b' :
                               subscriptionStatus.status === 'GRACE_PERIOD' ? '#92400e' : '#263143'
                      }}>
                        {new Date(subscriptionStatus.expiresAt).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {/* Grace Period End */}
                  {subscriptionStatus.graceEndsAt && subscriptionStatus.status === 'GRACE_PERIOD' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, color: '#92400e' }}>Grace ends</span>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#92400e' }}>
                        {new Date(subscriptionStatus.graceEndsAt).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {/* No subscription message */}
                  {(subscriptionStatus.status === 'NONE' ||
                    subscriptionStatus.status === 'EXPIRED' ||
                    subscriptionStatus.status === 'SUSPENDED' ||
                    subscriptionStatus.status === 'CANCELLED') && (
                    <div style={{
                      marginTop: 8,
                      padding: '10px 14px',
                      background: '#fff7ed',
                      borderRadius: 8,
                      borderLeft: '3px solid #f59e0b',
                      fontSize: 13,
                      color: '#92400e',
                      lineHeight: 1.5
                    }}>
                      {subscriptionStatus.message || 'Please contact support to manage your subscription.'}
                    </div>
                  )}
                  {renewError && (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#b91c1c' }}>{renewError}</div>
                  )}
                  {subscriptionRenewEligible && (
                  <div style={{ marginTop: 8, marginBottom: 2 }}>
                    <label style={{ fontSize: 12, color: '#64748b', marginRight: 8 }}>Payment:</label>
                    <select
                      value={subscriptionPaymentMethod}
                      onChange={(e) => setSubscriptionPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
                      style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    >
                      {renewalAllowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Stripe</option>}
                      {renewalAllowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto/Stablecoin</option>}
                    </select>
                  </div>
                )}
                {subscriptionRenewEligible && isCryptoPaymentMethod && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignSelf: 'stretch', maxWidth: 320 }}>
                    <input
                      value={cryptoBlockchain}
                      onChange={(e) => setCryptoBlockchain(e.target.value)}
                      placeholder="network"
                      style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    />
                    <input
                      value={cryptoSymbol}
                      onChange={(e) => setCryptoSymbol(e.target.value.toUpperCase())}
                      placeholder="symbol"
                      style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
                    />
                  </div>
                )}
                {subscriptionRenewEligible && (
                    <button
                      type="button"
                      disabled={renewBusy}
                      onClick={() => void startRenewalFlow()}
                      style={{
                        marginTop: 8,
                        alignSelf: 'flex-start',
                        padding: '10px 20px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#0dca9d',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: renewBusy ? 'not-allowed' : 'pointer',
                        opacity: renewBusy ? 0.7 : 1,
                      }}
                    >
                      {renewBusy ? 'Please wait…' : 'Renew subscription'}
                    </button>
                  )}
                  {subscriptionUpgradeEligible && !pendingUpgradeInfo?.hasPending && (
                    <button
                      type="button"
                      onClick={() => void openUpgradeModal()}
                      style={{
                        marginTop: 8,
                        alignSelf: 'flex-start',
                        padding: '10px 20px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#086afb',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      Upgrade plan
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Multi-Signature Wallets Section */}
            {userPermissions.includes('VIEW_RETAIL_DASHBOARD_MULTISIG_WALLETS') && (
              <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              marginBottom: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Multi-Signature Wallets</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div 
                  onClick={() => navigate('/multi-signature-wallets')}
                  style={{ 
                    flex: 1, 
                    minWidth: 100, 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🔐</div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                    My Signatory Wallets
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#263143' }}>
                    {dashboardData?.pendingMultiSigApprovals ? `${dashboardData.pendingMultiSigApprovals} pending` : 'View my wallets'}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
      {showUpgradeModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
          onClick={() => !upgradeFlowBusy && setShowUpgradeModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 440,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24,
              boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
          >
            <h2 id="upgrade-modal-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#263143' }}>
              Upgrade subscription
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              Choose a higher tier. If you originally joined on a product plan (e.g. Market Signals Pro), compliance
              will review first. Otherwise you can pay immediately with Stripe or Crypto.
            </p>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: '#475569', fontWeight: 600, marginBottom: 8 }}>Payment method</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {upgradeAllowedPaymentMethods.includes('STRIPE') && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#1e293b' }}>
                  <input
                    type="radio"
                    name="upgradePaymentMethod"
                    checked={subscriptionPaymentMethod === 'STRIPE'}
                    onChange={() => setSubscriptionPaymentMethod('STRIPE')}
                  />
                  Stripe
                </label>
                )}
                {upgradeAllowedPaymentMethods.includes('CRYPTO') && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#1e293b' }}>
                  <input
                    type="radio"
                    name="upgradePaymentMethod"
                    checked={subscriptionPaymentMethod === 'CRYPTO'}
                    onChange={() => setSubscriptionPaymentMethod('CRYPTO')}
                  />
                  Crypto / Stablecoin
                </label>
                )}
              </div>
            </div>
            {isCryptoPaymentMethod && (
              <div style={{ marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 6 }}>Blockchain</label>
                  <select
                    value={cryptoBlockchain}
                    onChange={(e) => setCryptoBlockchain(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                    <option value="tron">Tron</option>
                    <option value="bsc">BSC</option>
                    <option value="solana">Solana</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 6 }}>Token symbol</label>
                  <input
                    type="text"
                    value={cryptoSymbol}
                    onChange={(e) => setCryptoSymbol(e.target.value.toUpperCase())}
                    maxLength={12}
                    style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                    placeholder="USDT"
                  />
                </div>
              </div>
            )}
            {upgradeFlowError && (
              <div style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 14 }}>
                {upgradeFlowError}
              </div>
            )}
            {retailPlansForUpgrade.length === 0 ? (
              <p style={{ fontSize: 14, color: '#64748b' }}>
                No other subscription plans are available. Your administrator may need to add or activate tiers in
                subscription_plans.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {retailPlansForUpgrade.map((p) => {
                  const allowed = p.canSelectAsUpgrade === true;
                  return (
                    <label
                      key={p.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        padding: 12,
                        borderRadius: 10,
                        border: selectedUpgradePlanId === p.id ? '2px solid #086afb' : '1px solid #e2e8f0',
                        cursor: allowed ? 'pointer' : 'not-allowed',
                        background: selectedUpgradePlanId === p.id ? '#eff6ff' : '#fff',
                        opacity: allowed ? 1 : 0.65,
                      }}
                    >
                      <input
                        type="radio"
                        name="upgradePlan"
                        disabled={!allowed}
                        checked={selectedUpgradePlanId === p.id}
                        onChange={() => allowed && setSelectedUpgradePlanId(p.id)}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#263143' }}>{p.name}</div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {Number.isFinite(p.priceMonthly)
                            ? `${new Intl.NumberFormat('en-EU', {
                                style: 'currency',
                                currency: p.currency || 'EUR',
                              }).format(p.priceMonthly)} / month`
                            : 'Price on file'}
                        </div>
                        {!allowed && (
                          <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
                            Same or lower price than your current plan — pick a higher tier to upgrade.
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={upgradeFlowBusy}
                onClick={() => setShowUpgradeModal(false)}
                style={{
                  padding: '10px 18px',
                  borderRadius: 999,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  fontWeight: 600,
                  cursor: upgradeFlowBusy ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={upgradeFlowBusy || !canSubmitUpgrade}
                onClick={() => void confirmPlanUpgrade()}
                style={{
                  padding: '10px 18px',
                  borderRadius: 999,
                  border: 'none',
                  background: '#086afb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: upgradeFlowBusy || !canSubmitUpgrade ? 'not-allowed' : 'pointer',
                  opacity: upgradeFlowBusy || !canSubmitUpgrade ? 0.6 : 1,
                }}
              >
                {upgradeFlowBusy ? 'Please wait…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardStats: React.FC = () => {
  const [stats, setStats] = useState<DashboardStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adminApi = useAdminApiService();

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [dashboardResponse, portfolioResponse] = await Promise.all([
          adminApi.getDashboardStats(),
          adminApi.getPortfolioStats()
        ]);

        if (dashboardResponse.success && dashboardResponse.data && portfolioResponse.success && portfolioResponse.data) {
          setStats({
            ...dashboardResponse.data,
            totalCryptoPortfolio: portfolioResponse.data.totalCryptoValue,
            totalAssetPortfolio: portfolioResponse.data.totalAssetValue,
            cryptoGrowthRate: portfolioResponse.data.cryptoGrowth,
            assetGrowthRate: portfolioResponse.data.assetGrowth,
            // Set some default growth rates if not provided by the backend
            customerGrowthRate: 5.2,
            accountGrowthRate: 3.8
          });
        } else {
          setError(dashboardResponse.error || portfolioResponse.error || 'Failed to load dashboard stats');
        }
      } catch (err) {
        setError('Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [adminApi]);

  if (loading) return <div>Loading stats...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!stats) return <div>No stats available</div>;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '24px',
      padding: '24px',
      width: '100%'
    }}>
      <StatCard
        title="TOTAL CUSTOMERS"
        value={stats.totalCustomers}
        trend={stats.customerGrowthRate}
        icon="👥"
      />
      <StatCard
        title="ACTIVE CUSTOMERS"
        value={stats.activeCustomers}
        trend={stats.accountGrowthRate}
        icon="✓"
      />
      <StatCard
        title="TOTAL ACCOUNTS"
        value={stats.totalAccounts}
        trend={stats.accountGrowthRate}
        icon="💳"
      />
      <StatCard
        title="TOTAL CRYPTO PORTFOLIO"
        value={formatAmount(stats.totalCryptoPortfolio || 0, 'USD')}
        trend={stats.cryptoGrowthRate}
        icon="₿"
      />
      <StatCard
        title="TOTAL ASSET PORTFOLIO"
        value={formatAmount(stats.totalAssetPortfolio || 0, 'USD')}
        trend={stats.assetGrowthRate}
        icon="📈"
      />
    </div>
  );
};

export default Dashboard; 